import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState, useRef, useCallback } from "react";
import { FiMenu, FiX } from "react-icons/fi";
import { IQa } from "@/api/businessLinesApi";

interface SectionDndProps {
  qa: IQa;
  activeSectionId: string | null;
  setQa: (qa: IQa) => void;
  onSectionClick: (sectionId: string) => void;
  onSectionTitleChange: (sectionId: string, value: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onAddSection: () => void;
  editingSectionId: string | null;
  setEditingSectionId: (id: string | null) => void;
  isReadOnly?: boolean;
}

export const SectionDnd = ({
  qa,
  activeSectionId,
  setQa,
  onSectionClick,
  onSectionTitleChange,
  onDeleteSection,
  onAddSection,
  editingSectionId,
  setEditingSectionId,
  isReadOnly = false,
}: SectionDndProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const qaRef = useRef(qa);
  qaRef.current = qa;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (isReadOnly) return;
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReadOnly) return;

    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = qaRef.current.findIndex(
        (section) => section.id === active.id
      );
      const newIndex = qaRef.current.findIndex(
        (section) => section.id === over.id
      );

      const reorderedSections = arrayMove(qaRef.current, oldIndex, newIndex);

      // Update sequence_no for all sections and ensure unique titles
      const updatedSections = reorderedSections.map((section, index) => {
        // If the section has a generic title like "Section X", update it to match the new position
        let updatedTitle = section.title;
        if (section.title.match(/^Section \d+$/)) {
          updatedTitle = `Section ${index + 1}`;
        }

        return {
          ...section,
          sequence_no: index + 1,
          title: updatedTitle,
        };
      });

      setQa(updatedSections);
    }

    setActiveId(null);
  };

  const handleSectionClick = useCallback(
    (sectionId: string) => {
      onSectionClick(sectionId);
    },
    [onSectionClick]
  );

  return (
    <div className="w-[851px] h-12 relative rounded-2xl outline outline-1 outline-offset-[-1px] outline-slate-100 overflow-hidden mx-auto mb-6">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={qa.map((section) => section.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="left-0 top-0 absolute inline-flex justify-start items-center">
            {qa.map((section, idx) => (
              <SortableSection
                key={section.id}
                section={section}
                index={idx}
                isActive={activeSectionId === section.id}
                isEditing={editingSectionId === section.id}
                onSectionClick={handleSectionClick}
                onSectionTitleChange={onSectionTitleChange}
                onDeleteSection={onDeleteSection}
                onEditStart={() => setEditingSectionId(section.id)}
                onEditEnd={() => setEditingSectionId(null)}
                canDelete={qa.length > 1}
                isReadOnly={isReadOnly}
              />
            ))}
            {!isReadOnly && (
              <div
                className="w-14 h-12 relative bg-white overflow-hidden cursor-pointer"
                onClick={onAddSection}
              >
                <div className="left-[18px] top-[8px] absolute justify-center text-emerald-500 text-2xl font-semibold font-['Inter']">
                  +
                </div>
              </div>
            )}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeId ? (
            <div className="h-12 px-10 py-3 inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden relative bg-emerald-300 rounded-2xl">
              <div className="self-stretch inline-flex justify-start items-center gap-2">
                <div className="text-white text-sm font-semibold font-['Inter']">
                  {qa.find((s) => s.id === activeId)?.title ||
                    `Sec.${qa.findIndex((s) => s.id === activeId) + 1}`}
                </div>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

const SortableSection = ({
  section,
  index,
  isActive,
  isEditing,
  onSectionClick,
  onSectionTitleChange,
  onDeleteSection,
  onEditStart,
  onEditEnd,
  canDelete,
  isReadOnly = false,
}: {
  section: any;
  index: number;
  isActive: boolean;
  isEditing: boolean;
  onSectionClick: (sectionId: string) => void;
  onSectionTitleChange: (sectionId: string, value: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onEditStart: () => void;
  onEditEnd: () => void;
  canDelete: boolean;
  isReadOnly?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`h-12 px-10 py-3 inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden relative cursor-pointer group ${
        isActive ? "bg-emerald-300" : "bg-slate-100"
      }`}
      onClick={() => onSectionClick(section.id)}
    >
      <div className="self-stretch inline-flex justify-start items-center gap-2">
        {/* Drag handle - only visible on hover */}
        {!isReadOnly && (
          <div
            {...attributes}
            {...listeners}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-grab active:cursor-grabbing"
            title="Drag to reorder section"
          >
            <FiMenu size={14} className="text-gray-500" />
          </div>
        )}

        {isEditing && !isReadOnly ? (
          <input
            className="text-sm font-semibold font-['Inter'] rounded px-1 bg-transparent border-none outline-none"
            value={section.title}
            autoFocus
            onChange={(e) => onSectionTitleChange(section.id, e.target.value)}
            onBlur={onEditEnd}
            onKeyDown={(e) => {
              if (e.key === "Enter") onEditEnd();
            }}
            placeholder="Enter section title"
            title="Section title"
            aria-label="Section title"
          />
        ) : (
          <div
            className={`justify-center text-sm font-semibold font-['Inter'] ${
              isActive ? "text-white" : "text-zinc-500"
            }`}
            onDoubleClick={!isReadOnly ? onEditStart : undefined}
          >
            {section.title || `Sec.${index + 1}`}
          </div>
        )}
      </div>

      {/* Delete button for each section - only show if more than 1 section */}
      {canDelete && !isReadOnly && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDeleteSection(section.id);
          }}
          className="absolute right-1 top-1 p-1 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
          title="Delete section"
          aria-label="Delete section"
        >
          <FiX size={12} />
        </button>
      )}
    </div>
  );
};
